import { useModules } from "@/core/composables";
import { MODULE_ID_MARKETING_EXPERIENCE_API } from "@/core/constants/modules";
import { CustomPermissions } from "@/core/enums";
import menuData from "@/config/menu.json";
import { ROUTES } from "@/router/routes/constants";
import { useUser } from "@/shared/account";
import type { RouteRecordRaw } from "vue-router";
import type { ExtendedMenuLinkType } from "@/core/types";

const Dashboard = () => import("@/pages/account/innovadis/inno-dashboard.vue");
const Profile = () => import("@/pages/account/profile.vue");
const ChangePassword = () => import("@/pages/account/change-password.vue");
const Orders = () => import("@/pages/account/innovadis/inno-orders.vue");
const OrderDetails = () => import("@/pages/account/innovadis/inno-order-details.vue");
const DropOffPoint = () => import("@/pages/account/innovadis/inno-drop-off-point.vue");
const OrderPayment = () => import("@/pages/account/order-payment.vue");
const Backorders = () => import("@/pages/account/innovadis/inno-backorders.vue");
const Invoices = () => import("@/pages/account/innovadis/inno-invoices.vue");
const InvoiceDetails = () => import("@/pages/account/innovadis/inno-invoice-details.vue");
const AccountBulkOrder = () => import("@/pages/account/innovadis/inno-account-bulk-order.vue");
const Lists = () => import("@/pages/account/lists.vue");
const ListDetails = () => import("@/pages/account/innovadis/inno-list-details.vue");
const SavedCreditCards = () => import("@/pages/account/saved-credit-cards.vue");
const PromotionCoupons = () => import("@/pages/account/promotion-coupons.vue");

// Innovadis custom: extract typed route names from menu schema items used for redirect resolution
function getRouteName(item: ExtendedMenuLinkType): string | undefined {
  const route = item.route;

  if (typeof route === "object" && route !== null && "name" in route && typeof route.name === "string") {
    return route.name;
  }

  return undefined;
}

// Innovadis custom: keep menu item ordering aligned with configured priorities
function sortMenuItems(items: ExtendedMenuLinkType[]): ExtendedMenuLinkType[] {
  return [...items].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

// Innovadis custom: mirror account navigation visibility rules for route fallback logic
function canShowMenuItem(item: ExtendedMenuLinkType): boolean {
  const { checkPermissions, isCorporateMember, isMultiOrganization } = useUser();
  const hasPermission = item.requiredPermission ? checkPermissions(item.requiredPermission) : true;
  const isNotHiddenForCorporate = !(item.id === "addresses" && isCorporateMember.value);
  const isNotHiddenForMultiOrganization = !(item.id === "DropOffPoint" && !isMultiOrganization.value);

  return hasPermission && isNotHiddenForCorporate && isNotHiddenForMultiOrganization;
}

// Innovadis custom: resolve first visible account destination to avoid routing users to empty dashboard state
export function getFirstVisibleAccountRouteName(options?: { excludeDashboard?: boolean }): string {
  const { checkPermissions, isCorporateMember, user } = useUser();

  const accountMenuSections = menuData.header.desktop;

  const visibleMainItems = [
    ...sortMenuItems(accountMenuSections.purchasing.children ?? []).filter(canShowMenuItem),
    ...sortMenuItems(accountMenuSections.user.children ?? []).filter(canShowMenuItem),
    ...sortMenuItems(accountMenuSections.marketing.children ?? []).filter(canShowMenuItem),
  ];

  const firstMainRouteName = visibleMainItems
    .map(getRouteName)
    .find((routeName) => !!routeName && (!options?.excludeDashboard || routeName !== "Dashboard"));

  if (firstMainRouteName) {
    return firstMainRouteName;
  }

  const userIsProspect = !!user.value?.roles?.some((role) => role.name === "Prospect");
  const canViewCorporateSection =
    checkPermissions(CustomPermissions.ShowViewCompanyInformation) ||
    checkPermissions(CustomPermissions.ShowUserManagement);

  if (isCorporateMember.value && !userIsProspect && canViewCorporateSection) {
    const firstCorporateRouteName = sortMenuItems(accountMenuSections.corporate.children ?? [])
      .filter(canShowMenuItem)
      .map(getRouteName)
      .find((routeName) => !!routeName && (!options?.excludeDashboard || routeName !== "Dashboard"));

    if (firstCorporateRouteName) {
      return firstCorporateRouteName;
    }
  }

  return "Profile";
}

export const accountRoutes: RouteRecordRaw[] = [
  {
    path: "dashboard",
    name: "Dashboard",
    component: Dashboard,
  },
  { path: "change-password", name: "ChangePasswordAccount", component: ChangePassword },
  { path: "afzetpunt", name: "DropOffPoint", component: DropOffPoint },
  { path: "profile", name: "Profile", component: Profile },
  {
    path: "orders",
    // Innovadis custom: shield the orders route behind a permission check
    beforeEnter(_to, _from, next) {
      const { checkPermissions } = useUser();
      if (!checkPermissions(CustomPermissions.ShowOrderHistory)) {
        // Innovadis custom: unauthorized (by permissions) users are redirected to their first visible account page instead of Dashboard
        next({ name: getFirstVisibleAccountRouteName({ excludeDashboard: true }) });
      } else {
        next();
      }
    },
    children: [
      { path: "", name: "Orders", component: Orders },
      {
        path: ":orderId",
        children: [
          {
            path: "",
            name: "OrderDetails",
            component: OrderDetails,
            props: true,
          },
          {
            path: "payment",
            name: "OrderPayment",
            component: OrderPayment,
            props: true,
            meta: { layout: "Secure" },
          },
        ],
        meta: { hideLeftSidebar: true },
      },
    ],
  },
  {
    path: "backorders",
    children: [
      { path: "", name: "Backorders", component: Backorders },
      {
        path: ":orderId",
        children: [
          {
            path: "",
            name: "OrderDetails",
            component: OrderDetails,
            props: true,
          },
        ],
        meta: { hideLeftSidebar: true },
      },
    ],
  },
  {
    path: "invoices",
    // Innovadis custom: shield the invoices route behind a permission check
    beforeEnter(_to, _from, next) {
      const { checkPermissions } = useUser();
      if (!checkPermissions(CustomPermissions.ShowInvoiceHistory)) {
        // Innovadis custom: unauthorized users are redirected to their first visible account page instead of Dashboard
        next({ name: getFirstVisibleAccountRouteName({ excludeDashboard: true }) });
      } else {
        next();
      }
    },
    children: [
      { path: "", name: "Invoices", component: Invoices },
      {
        path: ":invoiceId",
        children: [
          {
            path: "",
            name: "InvoiceDetails",
            component: InvoiceDetails,
            props: true,
          },
        ],
        meta: { hideLeftSidebar: true },
      },
    ],
  },
  {
    path: "lists",
    // Innovadis custom: shield the lists route behind a permission check
    beforeEnter(_to, _from, next) {
      const { checkPermissions } = useUser();
      if (!checkPermissions(CustomPermissions.ShowWishlist)) {
        // Innovadis custom: unauthorized users are redirected to their first visible account page instead of Dashboard
        next({ name: getFirstVisibleAccountRouteName({ excludeDashboard: true }) });
      } else {
        next();
      }
    },
    children: [
      { path: "", name: "Lists", component: Lists },
      {
        path: ":listId",
        name: "ListDetails",
        component: ListDetails,
        props: true,
      },
    ],
  },
  {
    path: "bulk-order",
    name: "AccountBulkOrder",
    component: AccountBulkOrder,
    // Innovadis custom: shield the bulk order route behind a permission check
    beforeEnter(_to, _from, next) {
      const { checkPermissions } = useUser();
      if (!checkPermissions(CustomPermissions.ShowBulkOrder)) {
        // Innovadis custom: unauthorized users are redirected to their first visible account page instead of Dashboard
        next({ name: getFirstVisibleAccountRouteName({ excludeDashboard: true }) });
      } else {
        next();
      }
    },
  },
  {
    path: "saved-credit-cards",
    name: "SavedCreditCards",
    component: SavedCreditCards,
  },
  {
    path: ROUTES.PROMOTION_COUPONS.PATH,
    name: ROUTES.PROMOTION_COUPONS.NAME,
    component: PromotionCoupons,
    beforeEnter(_to, _from, next) {
      const { hasModule } = useModules();

      if (hasModule(MODULE_ID_MARKETING_EXPERIENCE_API)) {
        next();
      } else {
        next({ name: "NotFound" });
      }
    },
  },
];
